<template>
  <b-container class="mt-2" style="width:400px" v-if="isDataLoaded">
    <b-card v-if="lti_course" header="TWA Enrollment" class="text-center">
      <b-card-text class="text-left">
        <div class="mb-4 pb-3 border-bottom">
          <div><strong>Course:</strong> {{ lti_course.context_title }}</div>
          <div v-if="lti_course.teachers && lti_course.teachers.length">
            <strong>{{ lti_course.teachers.length === 1 ? 'Teacher:' : 'Teachers:' }}</strong>
            <span v-for="(teacher, index) in lti_course.teachers" :key="index">
              {{ teacher.display_name }}<template v-if="index < lti_course.teachers.length - 1">, </template>
            </span>
          </div>
          <div v-if="lti_course.products && lti_course.products.length">
            <strong>Unit(s):</strong>
            <span v-for="(product, index) in lti_course.products" :key="product.product_code">
              {{ product.name }}<template v-if="index < lti_course.products.length - 1">, </template>
            </span>
          </div>
        </div>
        <div class="mt-3" v-if="show_form === 'email'">
          <b>Verify Your Email</b>
          <p class="mt-2">Enter your email address to begin enrollment.</p>
          <b-input-group class="mt-2">
            <template #prepend>
              <b-input-group-text>
                Email
              </b-input-group-text>
            </template>
            <b-form-input name="email" v-model="form.email" type="email"></b-form-input>
          </b-input-group>
          <div class="recaptcha-wrapper mt-3">
            <vue-recaptcha ref="recaptcha" :sitekey=site_key @verify="onCaptchaVerified" @expired="onRecaptchaExpired"></vue-recaptcha>
          </div>
          <p v-if="email_error" class="mt-3 text-danger">{{ email_error }}</p>
          <div :class="email_error ? 'd-flex' : ''">
            <b-button variant="primary" :block="!email_error" class="flex-fill mr-2" @click="sendPin" :disabled="!isCaptchaVerified || isSending">Send Verification Code
              <b-spinner v-if="isSending" class="m-1" label="Spinning" small/>
            </b-button>
            <b-button v-if="email_error" variant="outline-primary" class="flex-fill" @click="openBeacon">
              Contact TWA
            </b-button>
          </div>
        </div>
        <div v-if="show_form === 'magic_code'">
          <b>Enter Verification Code</b>
          <p class="mt-2">Check your email. We sent a code to {{ form.email }}</p>
          <div class="pin-input-container my-3">
            <input
              v-for="(digit, index) in pinDigits"
              :key="index"
              :ref="'pin' + index"
              type="text"
              inputmode="numeric"
              maxlength="1"
              class="pin-digit"
              :value="pinDigits[index]"
              @input="onDigitInput($event, index)"
              @keydown="onDigitKeydown($event, index)"
              @paste="onPaste($event)"
              autocomplete="off"
            />
          </div>
          <p v-if="magic_error" class="text-danger">{{ magic_error }}</p>
          <div :class="magic_error ? 'd-flex' : ''">
            <b-button variant="primary" :block="!magic_error" class="flex-fill mr-2" @click="verifyPin" :disabled="!form.pin_number || form.pin_number.length !== 6 || isSending">Verify Code
              <b-spinner v-if="isSending" class="m-1" label="Spinning" small/>
            </b-button>
            <b-button v-if="magic_error" variant="outline-primary" class="flex-fill" @click="openBeacon">
              Contact TWA
            </b-button>
          </div>
        </div>
      </b-card-text>
    </b-card>
  </b-container>
  <b-container class="mt-2" style="width:400px" v-else>
    <b-card header="TWA Enrollment" class="text-center">
      <div v-if="errorMessage">
        <h5>{{ errorContent.title }}</h5>
        <p v-if="errorContent.subtitle">{{ errorContent.subtitle }}</p>
        <b-card-text>
          <!-- Special content for join_link_expired -->
          <div v-if="error_reason === 'join_link_expired'">
            <p><strong>Already signed up?</strong> Login here <a :href="login_url" target="_blank" rel="noopener noreferrer">{{ login_url }}</a></p>
            <p><strong>Need to sign up?</strong> Contact your teacher to request an extension.</p>
          </div>

          <!-- Action buttons -->
          <div class="mt-3">
            <b-button v-if="canRetry" variant="primary" class="mr-2" @click="getCourse" :disabled="isRetrying">Try Again
              <b-spinner v-if="isRetrying" class="m-1" label="Retrying" small/>
            </b-button>
            <b-button v-if="errorMessage" variant="outline-primary" @click="openBeacon">
              Contact TWA
            </b-button>
          </div>

          <!-- Debug info (only in development) -->
          <div v-if="isDevelopment && beaconError" class="text-muted small mt-3 text-left">
            <div><strong>Beacon Data:</strong></div>
            <div>User Message: {{ beaconError['User Message'] }}</div>
            <div>Error Context: {{ beaconError['Error Context'] }}</div>
            <div>Error Code: {{ beaconError['Error Code'] || '(none)' }}</div>
            <div v-if="beaconError['Error Message']">Error Message: {{ beaconError['Error Message'] }}</div>
            <div v-if="beaconError['Term']">Term: {{ beaconError['Term'] }}</div>
            <div>HTTP Status: {{ beaconError['HTTP Status'] || '(none)' }}</div>
          </div>
        </b-card-text>
      </div>
      <div v-else>
        Hang on. Getting info...
        <b-spinner class="ml-2" style="width: 1.5rem; height: 1.5rem;"
                   variant="secondary"></b-spinner>
      </div>
    </b-card>
  </b-container>
</template>

<script>
import {getLtiCourse, sendCode, verifyCode} from "../utils/lti-api"
import { VueRecaptcha } from 'vue-recaptcha'
import {getErrorMessage, getMessageForCode} from "../utils/error-messages"
import {initBeacon, setBeaconData, clearBeaconError, openBeacon, identifyBeaconUser, reportErrorToBeacon, reportValidationErrorToBeacon} from "../utils/beacon"

export default {
  components: {
    VueRecaptcha
  },
  data() {
    return {
      form: {
        email: null,
        pin_number: null
      },
      recaptcha_response: null,
      pinDigits: ['', '', '', '', '', ''],
      email_error: null,
      magic_error: null,
      isDataLoaded: false,
      isSending: false,
      isRetrying: false,
      show_form: 'email',
      icon: ['fa', 'envelope'],
      code: null,
      canvas_course: null,
      lti_course: null,
      canvas_domain: null,
      enrollment: null,
      errorMessage: null,
      error_reason: null,
      beaconError: null,  // Debug: stores last error sent to Beacon
      isCaptchaVerified: false,
      beaconContext: {},
    }
  },
  watch: {
    'form.email': function (newName) {
      this.form.email = newName.toLowerCase();
    }
  },
  computed: {
    token() {
      return this.$route.query.course || null;
    },
    isDevelopment() {
      return process.env.NODE_ENV === 'development';
    },
    email_domain() {
      if (this.lti_course && this.lti_course.lti_consumer) {
        return this.lti_course.lti_consumer.email_domain || null
      }
      return null
    },
    site_key() {
      return process.env.VUE_APP_RECAPTCHA_KEY;
    },
    login_url() {
      // For already enrolled students, direct them to the course's actual Canvas domain
      if (this.canvas_domain) {
        return  'https://' + this.canvas_domain
      }
      return ""
    },
    canRetry() {
      // Show retry button only for transient errors
      const retryableErrors = ['network_error', 'canvas_api_error', 'database_unavailable', 'stripe_api_error']
      return retryableErrors.includes(this.error_reason)
    },
    errorContent() {
      return {
        title: 'Hmm...',
        subtitle: this.errorMessage
      }
    }
  },
  methods: {
    clearErrorState() {
      this.error_reason = null
      this.errorMessage = null
      this.beaconError = null
    },
    notifyMe(message, variant = 'danger') {
      this.$bvToast.toast(message, {
        title: 'Notification',
        solid: true,
        variant: variant,
        autoHideDelay: 2500,
        appendToast: true
      })
    },
    async getCourse() {
      this.isRetrying = true
      this.clearErrorState()

      // Small delay for smoother UX transition
      await new Promise(resolve => setTimeout(resolve, 500))

      if (!this.token) {
        this.setError('no_token')
        this.isRetrying = false
        return
      }

      try {
        this.lti_course = await getLtiCourse(this.token)
        // Backend now validates - if we get here, course is valid
        this.errorMessage = null
        this.error_reason = null
        this.isDataLoaded = true

        // Set Beacon session data now that course is loaded
        this.buildSessionDataForBeacon()

        // Clear error data from Beacon on success
        clearBeaconError(this.beaconContext)
      } catch (error) {
        this.handleFetchError(error)
      }
      this.isRetrying = false
    },
    handleFetchError(error) {
      this.errorMessage = getErrorMessage(error)
      const errorCode = error.response?.data?.error?.code

      if (errorCode) {
        this.error_reason = errorCode

        // Store canvas_domain for expired join links (used in login link)
        if (errorCode === 'join_link_expired' && error.response.data?.canvas_domain) {
          this.canvas_domain = error.response.data.canvas_domain
        }
      } else {
        this.error_reason = 'network_error'
      }

      this.beaconError = reportErrorToBeacon(error, errorCode ? 'Course Lookup Error' : 'Server Error', this.beaconContext)
    },
    setError(reason) {
      this.errorMessage = getMessageForCode(reason)
      this.error_reason = reason
    },
    async sendPin() {
      this.isSending = true
      try {
        // Validate reCAPTCHA (defense in depth)
        if (!this.recaptcha_response) {
          this.email_error = 'Please complete the reCAPTCHA verification'
          this.isSending = false
          return
        }

        if (this.checkEmailForm()) {
          await sendCode(this.token, this.form.email, this.recaptcha_response)
          this.show_form = 'magic_code'

          // Clear Beacon error data on success
          clearBeaconError(this.beaconContext)
        }
      } catch (error) {
        this.email_error = getErrorMessage(error)
        this.beaconError = reportErrorToBeacon(error, 'Send Code Error', this.beaconContext)
        this.show_form = 'email'
      }
      this.isSending = false
    },
    async verifyPin() {
      this.isSending = true
      try {
        if (this.checkMagicForm()) {
          let response = await verifyCode(this.token, this.form.email, this.form.pin_number)
          if (response.lti_enrollment) {
            // Clear Beacon error data before navigating to Pay page
            clearBeaconError(this.beaconContext)
            this.$router.push({path: '/pay', query: {token: response.lti_enrollment.token}})
          }
        }
      } catch (error) {
        this.magic_error = getErrorMessage(error)
        this.beaconError = reportErrorToBeacon(error, 'Verify Code Error', this.beaconContext)
      }
      this.isSending = false
    },
    checkEmailForm() {
      this.email_error = null

      if (!this.form.email || !this.validateEmail(this.form.email)) {
        this.email_error = 'Email does not look right.'
        return false
      }

      // Check email domain restriction if set
      if (this.email_domain) {
        const patterns = this.email_domain.split(',').map(p => p.trim()).filter(p => p)
        const emailDomain = this.form.email.split('@')[1]?.toLowerCase()

        const matches = patterns.some(pattern => {
          if (pattern.startsWith('.')) {
            // Suffix pattern: ".edu" matches any .edu domain
            return emailDomain.endsWith(pattern.toLowerCase())
          }
          // Exact domain: "stanford.edu"
          return emailDomain === pattern.toLowerCase()
        })

        if (!matches) {
          const formatted = patterns.map(p => p.startsWith('.') ? `*${p}` : `@${p}`).join(' or ')
          this.email_error = `Email must end with ${formatted}`
          this.beaconError = reportValidationErrorToBeacon(this.email_error, 'Email Domain Validation', this.beaconContext)
          return false
        }
      }

      return true
    },
    checkMagicForm() {
      this.magic_error = null

      if (!this.form.pin_number) {
        this.magic_error = 'Please enter the verification code from your email.'
        return false
      }

      // Check for exactly 6 digits
      if (!/^\d{6}$/.test(this.form.pin_number)) {
        this.magic_error = 'Please enter all 6 digits.'
        return false
      }

      return true
    },
    validateEmail(email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    },
    onDigitInput(event, index) {
      const value = event.target.value.replace(/\D/g, '');
      this.$set(this.pinDigits, index, value.charAt(0) || '');
      this.updatePinNumber();

      // Auto-advance to next input
      if (value && index < 5) {
        this.$refs['pin' + (index + 1)][0].focus();
      }
    },
    onDigitKeydown(event, index) {
      // Backspace on empty input goes to previous
      if (event.key === 'Backspace' && !this.pinDigits[index] && index > 0) {
        this.$refs['pin' + (index - 1)][0].focus();
      }
    },
    onPaste(event) {
      event.preventDefault();
      const paste = (event.clipboardData || window.clipboardData).getData('text');
      const digits = paste.replace(/\D/g, '').slice(0, 6).split('');

      digits.forEach((d, i) => {
        this.$set(this.pinDigits, i, d);
      });
      this.updatePinNumber();

      // Focus last filled or next empty
      const focusIndex = Math.min(digits.length, 5);
      this.$refs['pin' + focusIndex][0].focus();
    },
    updatePinNumber() {
      this.form.pin_number = this.pinDigits.join('');
    },
    onCaptchaVerified(response) {
      // Enable form submission when CAPTCHA challenge is successfully completed
      if (response) {
        this.recaptcha_response = response;
        this.isCaptchaVerified = true;
      }
    },
    onRecaptchaExpired() {
      this.isCaptchaVerified = false;
    },
    buildSessionDataForBeacon() {
      // Guard: only build session data if course loaded successfully
      if (!this.lti_course) return

      // Store context for use in error calls (merge-vs-replace semantics undocumented; every call passes the full set)
      this.beaconContext = {
        'Flow': 'Direct Join',
        'Course': this.lti_course.context_title,
        'Domain': this.lti_course.canvas_domain,
        'Course Id': this.lti_course.canvas_course_id,
        'Course Token': this.token
      }

      setBeaconData(this.beaconContext)

      // Identify user by email if available
      if (this.form.email) {
        identifyBeaconUser(this.form.email)
      }
    },
    openBeacon
  },
  mounted() {
    initBeacon()
    // Minimal context immediately so support knows which course link was attempted.
    // Stored on beaconContext so a pre-load error report re-sends it — session-data
    // merge-vs-replace is undocumented, so every call passes the full set; an error
    // report with empty context could otherwise wipe it.
    if (this.token) {
      this.beaconContext = { 'Flow': 'Direct Join', 'Course Token': this.token }
      setBeaconData(this.beaconContext)
    }
    this.getCourse()
  }
};
</script>

<style scoped>
/* Scale reCAPTCHA slightly larger */
.recaptcha-wrapper {
  transform: scale(1.08);
  transform-origin: 0 0;
  margin-bottom: 10px;
}

.pin-input-container {
  display: flex;
  justify-content: space-between;
}

.pin-digit {
  width: 45px;
  height: 48px;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  border: 2px solid #ced4da;
  border-radius: 8px;
}

.pin-digit:focus {
  border-color: #28a745;
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
}
</style>
